﻿import {ElementNode, DOMExportOutput, EditorConfig, LexicalNode, NodeKey, SerializedElementNode} from "lexical";

export type SerializedImageNode = SerializedElementNode & {
    type: 'image';
    version: 1;
    src: string;
    altText?: string;
    width?: number;
    height?: number;
};

export class ImageNode extends ElementNode {
    src: string;
    altText?: string;
    width?: number;
    height?: number;

    constructor(src: string, altText?: string, width?: number, height?: number, key?: NodeKey) {
        super(key);
        this.src = src;
        this.altText = altText;
        this.width = width;
        this.height = height;
    }

    static getType(): string {
        return 'image';
    }

    static clone(node: ImageNode): ImageNode {
        return new ImageNode(node.src, node.altText, node.width, node.height, node.__key);
    }

    static importJSON(serializedNode: SerializedImageNode): ImageNode {
        const {src, altText, width, height} = serializedNode;
        return new ImageNode(src, altText, width, height);
    }

    exportJSON(): SerializedImageNode {
        return {
            ...super.exportJSON(),
            type: 'image',
            version: 1,
            src: this.src,
            altText: this.altText,
            width: this.width,
            height: this.height,
        };
    }

    setResizing(isResizing: boolean): void {
        const writable = this.getWritable();
        (writable as any).__isResizing = isResizing;
    }

    setWidthAndHeight(width: number, height: number): void {
        const writable = this.getWritable();
        writable.width = width;
        writable.height = height;
    }

    createDOM(): HTMLElement {
        const container = document.createElement("div");
        container.className = "image-node-container";
        container.style.display = "inline-block";
        container.style.position = "relative";
        container.style.userSelect = "none";

        const img = document.createElement("img");
        img.src = this.src;
        if (this.altText) img.alt = this.altText;
        img.style.display = "block";

        if (this.width) {
            img.style.width = `${this.width}px`;
        } else {
            img.style.maxWidth = "100%";
        }

        if (this.height) {
            img.style.height = `${this.height}px`;
        }

        container.appendChild(img);

        // Resizer handles
        const resizer = document.createElement("div");
        resizer.className = "image-resizer";

        const handle = document.createElement("div");
        handle.className = "image-resizer-handle";
        resizer.appendChild(handle);

        container.appendChild(resizer);

        let isResizing = false;
        let startX: number;
        let startY: number;
        let startWidth: number;
        let startHeight: number;

        handle.addEventListener("mousedown", (e: MouseEvent) => {
            e.preventDefault();
            isResizing = true;
            startX = e.clientX;
            startY = e.clientY;
            startWidth = img.clientWidth;
            startHeight = img.clientHeight;

            const onMouseMove = (moveEvent: MouseEvent) => {
                if (!isResizing) return;
                const deltaX = moveEvent.clientX - startX;
                const deltaY = moveEvent.clientY - startY;

                const newWidth = Math.max(50, startWidth + deltaX);
                const aspectRatio = startWidth / startHeight;
                const newHeight = newWidth / aspectRatio;

                img.style.width = `${newWidth}px`;
                img.style.height = `${newHeight}px`;
            };

            const onMouseUp = () => {
                if (isResizing) {
                    isResizing = false;
                    document.removeEventListener("mousemove", onMouseMove);
                    document.removeEventListener("mouseup", onMouseUp);

                    // Update Lexical state
                    const editor = (window as any).LexicalEditorInstance; // We might need a better way to get the editor
                    if (editor) {
                        editor.update(() => {
                            const node = this.getLatest();
                            if (node instanceof ImageNode) {
                                node.setWidthAndHeight(img.clientWidth, img.clientHeight);
                            }
                        });
                    }
                }
            };

            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
        });

        return container;
    }

    updateDOM(prevNode: ImageNode, dom: HTMLElement): boolean {
        const img = dom.querySelector("img");
        if (img) {
            if (this.width !== prevNode.width) {
                img.style.width = this.width ? `${this.width}px` : "";
            }
            if (this.height !== prevNode.height) {
                img.style.height = this.height ? `${this.height}px` : "";
            }
        }
        return false;
    }
}

export function $createImageNode(src: string, altText?: string): ImageNode {
    return new ImageNode(src, altText);
}

export function $isImageNode(node?: LexicalNode | null): node is ImageNode {
    return node instanceof ImageNode;
}
